import React, { useState, useEffect, createRef } from 'react';
import './Students.scss';
import { TablePagination } from '@material-ui/core';
import useDebounce from '../../hooks/useDebounce';
import _ from 'lodash';
import EnhancedTableHead from './TableHead';
import Action from './Action';

const qs = require('query-string');
const REACT_APP_MY_URL = process.env.REACT_APP_MY_URL;
const container = createRef();

const search = window.location.search;
const params = new URLSearchParams(search);
const groupId = params.get('group_id') || null;
const active = params.get('active') || true;

const getTextPlaceholderStyles = (key, column) => {
    let ret = {
        animationDelay: `${key++ * 0.25}s`,
        animationDuration: `${0.5}s`,
        paddingLeft: '50px',
        paddingRight: '50px',
    };
    if (column === 'name') {
        ret.paddingLeft = `${_.clamp(Math.random() * 100, 20, 50)}px`;
        ret.paddingRight = `${_.clamp(Math.random() * 100, 20, 50)}px`;
    }
    if (column === 'actions') {
        ret.paddingLeft = `${_.clamp(Math.random() * 100, 30, 40)}px`;
        ret.paddingRight = `${_.clamp(Math.random() * 100, 30, 40)}px`;
    }

    return ret;
};

export default ({ familyId }) => {
    const REACT_APP_API_URL = `${process.env.REACT_APP_API_URL}/api/families/${familyId}/customers?groupId=${groupId}&active=${active}`;

    const [columns] = useState([
        { name: 'first_name', title: 'First Name' },
        { name: 'last_name', title: 'Last Name' },
        { name: 'code', title: 'Code' },
        { name: 'plan_id', title: 'Plan ID' },
        { name: 'id', title: 'ID' },
        { name: 'active', title: 'Active' },
        { name: 'nwea_bid', title: 'NWEA BID' },
    ]);
    const [placeHolderRows, setPlaceHolderRows] = useState(10);
    const [students, setStudents] = useState([]);
    const [totalCount, setTotalCount] = useState(0);
    const [pageSize, setPageSize] = useState(10);
    const [currentPage, setCurrentPage] = useState(0);
    const [loading, setLoading] = useState(false);
    const [lastQuery, setLastQuery] = useState();
    const [pageSizes, setPageSizes] = useState([]);
    const [searchValue, setSearchValue] = useState('');
    const [isSearching, setIsSearching] = useState(false);
    const [searchTotalCount, setSearchTotalCount] = useState(0);

    const [order, setOrder] = React.useState('asc');
    const [orderBy, setOrderBy] = React.useState('last_name');

    const debouncedSearchValue = useDebounce(searchValue, 500);
    const debouncedPageSize = useDebounce(pageSize, 1000);
    const debouncedCurrentPage = useDebounce(currentPage, 250);
    const debouncedOrder = useDebounce(order, 250);
    const debouncedOrderBy = useDebounce(orderBy, 250);

    const params = {
        perPage: pageSize,
        page: currentPage,
        columns: columns.map((e) => {
            return e.name;
        }),
        search: searchValue,
        order: order,
        orderBy: orderBy,
    };

    const loadData = () => {
        let queryString = `${REACT_APP_API_URL}&${qs.stringify(params, { arrayFormat: 'separator', arrayFormatSeparator: ',' })}`;
        if (queryString !== lastQuery && !loading) {
            setLoading(true);
            fetch(queryString)
                .then((response) => response.json())
                .then(({ data, total: newTotalCount }) => {
                    setStudents(data);
                    setPlaceHolderRows(0);
                    if (isSearching) {
                        setSearchTotalCount(newTotalCount);
                        setTotalCount(0);
                    }
                    if (!isSearching) {
                        setSearchTotalCount(0);
                        setTotalCount(newTotalCount);
                    }
                    setLoading(false);
                    window.parent.postMessage({ eventName: 'heightMessage', value: container.current.clientHeight }, '*');
                })
                .catch(() => setLoading(false));
            setLastQuery(queryString);
        }
    };

    const handleRequestSort = (event, property) => {
        const isAsc = orderBy === property && order === 'asc';
        setOrder(isAsc ? 'desc' : 'asc');
        setOrderBy(property);
    };

    const navigate = (url) => {
        window.parent.postMessage({ eventName: 'navigateMessage', value: url }, '*');
    };

    useEffect(() => loadData(), [debouncedCurrentPage, debouncedPageSize, debouncedSearchValue, debouncedOrder, debouncedOrderBy]);

    useEffect(() => {
        if (!isSearching) {
            setPageSizes([10, 25, 50, { value: totalCount, label: 'All' }]);
        }
    }, [totalCount]);

    useEffect(() => {
        if (isSearching) {
            setPageSizes([]);
        }
    }, [searchTotalCount]);

    useEffect(() => {
        setStudents([]);
        setPlaceHolderRows(_.clamp(pageSize, 50));
    }, [currentPage, pageSize, searchValue, order, orderBy]);

    useEffect(() => {
        if (searchValue) {
            setIsSearching(true);
        }
        if (!searchValue) {
            setIsSearching(false);
        }
    }, [searchValue]);

    useEffect(() => {
        if (isSearching) {
            setCurrentPage(0);
            params.page = 0;
            delete params.perPage;
            container.current.classList.add('isSearching');
        }
        if (!isSearching) {
            params.perPage = pageSize;
            container.current.classList.remove('isSearching');
        }
    }, [isSearching]);

    useEffect(() => {
        console.log(order);
        console.log(orderBy);
    }, [order, orderBy]);

    return (
        <div ref={container}>
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                <tbody>
                    <tr>
                        <td>
                            <TablePagination
                                labelRowsPerPage={'Students per page'}
                                rowsPerPageOptions={pageSizes}
                                component="div"
                                count={searchTotalCount || totalCount}
                                page={currentPage}
                                onChangePage={(e, value) => {
                                    setCurrentPage(value);
                                }}
                                rowsPerPage={searchTotalCount || pageSize}
                                onChangeRowsPerPage={(e) => {
                                    setPageSize(parseInt(e.target.value));
                                    setCurrentPage(0);
                                }}
                            />
                        </td>
                        <td align={`right`}>
                            <input
                                autoComplete="off"
                                autoCorrect="off"
                                autoCapitalize="off"
                                spellCheck="false"
                                onChange={(e) => {
                                    setSearchValue(e.target.value);
                                }}
                                className={`searchInput`}
                                placeholder={`Search`}
                            />
                        </td>
                    </tr>
                </tbody>
            </table>
            <div className={`students`}>
                <table style={{ width: '100%' }}>
                    <EnhancedTableHead order={order} orderBy={orderBy} onRequestSort={handleRequestSort} />
                    <tbody>
                        {[...Array(placeHolderRows).keys()].map((row, key) => (
                            <tr key={key}>
                                <td>
                                    <span style={getTextPlaceholderStyles(key, 'name')} className={`textPlaceholder`}>
                                        &nbsp;
                                    </span>
                                    &nbsp;
                                    <span style={getTextPlaceholderStyles(key, 'name')} className={`textPlaceholder`}>
                                        &nbsp;
                                    </span>
                                </td>
                                <td>
                                    <span style={getTextPlaceholderStyles(key, 'actions')} className={`textPlaceholder`}>
                                        &nbsp;
                                    </span>
                                    &nbsp;
                                    <span style={getTextPlaceholderStyles(key, 'actions')} className={`textPlaceholder`}>
                                        &nbsp;
                                    </span>
                                    &nbsp;
                                    <span style={getTextPlaceholderStyles(key, 'actions')} className={`textPlaceholder`}>
                                        &nbsp;
                                    </span>
                                    &nbsp;
                                    <span style={getTextPlaceholderStyles(key, 'actions')} className={`textPlaceholder`}>
                                        &nbsp;
                                    </span>
                                </td>
                            </tr>
                        ))}
                        {students.map((student, key) => (
                            <tr key={key}>
                                <td>
                                    {student.last_name}, {student.first_name}
                                </td>
                                <td>
                                    <div className={`actions`}>
                                        <table>
                                            <tbody>
                                                <tr>
                                                    {student.available_actions.map((action, key) => (
                                                        <td key={key}>
                                                            <Action student={student} action={action} domain={REACT_APP_MY_URL} />
                                                        </td>
                                                    ))}
                                                </tr>
                                            </tbody>
                                        </table>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};
